import { App } from 'vue'

import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import { IonicVue } from '@ionic/vue'
import router from '../router/router'
import w from '@/plugins/w/w'
import wTippy from '@/plugins/app@components/vue-tippy'
import libNotifications from '@/plugins/lib@notifications'
import MedirexClientzone from '@medirex/vue-components'
// import MedirexClientzone from '@/plugins/@medirex/clientzone'
import FloatingVue from 'floating-vue'
import VueGoodTablePlugin from 'vue-good-table-next'
import { VueQueryPlugin } from '@tanstack/vue-query'
import sk from '@/plugins/app/_locales/sk.json'
import en from '@/plugins/app/_locales/en.json'
import axios from 'axios'

// Available languages in the application
const AVAILABLE_LANGUAGES = ['sk', 'en']

/**
 * Detects the best matching language based on browser preferences
 * @returns The detected language code or 'sk' as default
 */
function detectLanguage(): string {
	// Check if user has already set a language preference
	const savedLanguage = localStorage.getItem('language')
	if (savedLanguage && AVAILABLE_LANGUAGES.includes(savedLanguage)) {
		return savedLanguage
	}

	// Get browser language preferences
	const browserLanguages = navigator.languages || [navigator.language]

	// Try to find a match for the browser language
	for (const browserLang of browserLanguages) {
		// Extract the base language code (e.g., 'en' from 'en-US')
		const baseLang = browserLang.split('-')[0].toLowerCase()

		// Check for exact match first
		if (AVAILABLE_LANGUAGES.includes(browserLang.toLowerCase())) {
			return browserLang.toLowerCase()
		}

		// Check for base language match
		if (AVAILABLE_LANGUAGES.includes(baseLang)) {
			return baseLang
		}
	}

	// Default to Slovak if no match found
	return 'sk'
}

export default (app: App) => {
	const pinia = createPinia()

	const pluralizationRules = {
		sk: function (choice, choicesLength) {
			choice = Math.abs(choice)

			if (choice === 1) {
				return 0 // Singular
			} else if (choice >= 2 && choice <= 4 && !(Math.floor(choice / 10) === 1)) {
				return 1 // Plural for small numbers (2-4)
			} else {
				return 2 // Plural for other cases
			}
		}
	}

	// Detect the appropriate language
	const detectedLanguage = detectLanguage()

	// Save the detected language to localStorage for future use
	if (!localStorage.getItem('language')) {
		localStorage.setItem('language', detectedLanguage)
	}

	const i18n = createI18n({
		legacy: false,
		locale: detectedLanguage,
		fallbackLocale: 'en',
		messages: {
			sk,
			en
		},
		pluralRules: pluralizationRules
	})

	axios.interceptors.request.use(config => {
		if (i18n.global.locale.value) {
			config.headers['Accept-Language'] = i18n.global.locale.value
		}

		return config
	})

	const floatingVueOptions = {
		themes: {
			'info-tooltip': {
				$extend: 'tooltip',
				$resetCss: true
			}
		}
	}

	const wOptions = {
		i18n: {
			disabled: true,
			defaultLanguage: 'sk',
			languages: [
				{ title: 'Slovenský', flag: 'sk', value: 'sk' },
				{ title: 'English', flag: 'en', value: 'en' }
			]
		},
		toast: {
			i18n: i18n
		}
	}

	app.use(pinia)
		.use(IonicVue)
		.use(router)
		.use(i18n)
		.use(w, wOptions)
		.use(VueQueryPlugin)
		.use(VueGoodTablePlugin)
		.use(MedirexClientzone)
		.use(libNotifications)
		.use(wTippy)
		.use(FloatingVue, floatingVueOptions)
}
